import React, { useEffect, useMemo, useState } from "react";

import { useGetIdentity, useNotification, useOne, useTranslation } from "@refinedev/core";

import Person from "@mui/icons-material/Person";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { AddCoverageDialog, CardButton, RemoveCoverageDialog } from "@/components/common";
import type { CoverageAction } from "@/components/common";
import { useOrganizationPractitionersContext } from "@/contexts";
import { AlertEmailPreference, useAlertEmailPreference } from "@/hooks/useAlertEmailPreference";
import { useCoverage } from "@/hooks/useCoverage";
import { FIVE_MINUTE_STALE_TIME } from "@/providers/data/fhir-data-provider";
import type { IUser } from "@/types/types";
import { FhirResourceType, extractFhirResourceId, formatPractitionerName } from "@/utils/fhir-utils";

import * as Sentry from "@sentry/react";
import { fhirR4 } from "@smile-cdr/fhirts";

export const SettingsPanel: React.FC = () => {
  const { translate } = useTranslation();
  const { open: openNotification } = useNotification();
  const { data: identity } = useGetIdentity<IUser>();
  const { practitioners } = useOrganizationPractitionersContext();
  const { preference, isLoading, isUpdating, hasPermission, updatePreference } = useAlertEmailPreference();

  const practitionerId = identity?.fhirPractitionerId
    ? extractFhirResourceId(identity.fhirPractitionerId, FhirResourceType.Practitioner)
    : null;

  // Always include the logged-in practitioner's own id so their coverage is fetched
  // even when they are absent from the org practitioner list (e.g. the context filters
  // strictly to active === true, or their role is scoped to a child clinic). Without
  // this, coverageMap has no entry for them and their assigned coverage never shows.
  const practitionerIds = useMemo(() => {
    const ids = practitioners.map(p => p.id);
    if (practitionerId && !ids.includes(practitionerId)) {
      ids.push(practitionerId);
    }
    return ids;
  }, [practitioners, practitionerId]);

  const {
    coverageMap,
    isLoading: isCoverageLoading,
    isSaving,
    setCoverage,
    removeCoverage,
  } = useCoverage({
    practitionerIds,
    enabled: practitionerIds.length > 0,
  });

  const coveringPractitionerId = practitionerId ? (coverageMap.get(practitionerId) ?? null) : null;

  // Filter out the logged-in practitioner and away practitioners from coverage options
  const availablePractitioners = useMemo(() => {
    return practitioners.filter(
      p => p.id !== practitionerId && (!coverageMap.has(p.id) || p.id === coveringPractitionerId)
    );
  }, [practitioners, practitionerId, coverageMap, coveringPractitionerId]);

  const [selectedClinician, setSelectedClinician] = useState<string>("");

  // Optimistic override for the brief window between a save/remove action and the
  // coverage query catching up. null defers to the server-derived state below.
  const [optimisticCovered, setOptimisticCovered] = useState<boolean | null>(null);

  // Whether coverage is currently assigned. Derived from the fetched coverage data so
  // it always reflects the latest read — and recovers if the first fetch was slow or
  // failed — rather than latching once. The optimistic override wins during a pending
  // write so the UI updates immediately on save/remove.
  const isCovered = optimisticCovered ?? coveringPractitionerId != null;

  // Coverage confirmation dialog state
  const [coverageConfirmOpen, setCoverageConfirmOpen] = useState(false);
  const [coverageConfirmAction, setCoverageConfirmAction] = useState<CoverageAction>("add");
  // Email preference chosen inside the coverage dialog (defaults: add → None, remove → All Alerts)
  const [dialogEmailPreference, setDialogEmailPreference] = useState<AlertEmailPreference>(AlertEmailPreference.NONE);

  // Name of the currently-assigned coverage practitioner (for the saved display and
  // remove dialog). Captured into state when coverage is set/loaded so it stays
  // stable even if the practitioner later drops out of the org list.
  const [coveragePractitionerName, setCoveragePractitionerName] = useState<string>("");

  // The covering practitioner's name from the org list, when present.
  const coveringNameFromList = useMemo(
    () => (coveringPractitionerId ? practitioners.find(p => p.id === coveringPractitionerId)?.name : undefined),
    [practitioners, coveringPractitionerId]
  );

  // Fallback: fetch the covering practitioner directly when they are absent from the
  // org list (e.g. filtered out by active status, or scoped to a different clinic).
  // Without this, coverage assigned by an admin renders with a blank name.
  const { result: fetchedCoveringPractitioner } = useOne<fhirR4.Practitioner>({
    resource: FhirResourceType.Practitioner,
    id: coveringPractitionerId ?? "",
    queryOptions: {
      enabled: !!coveringPractitionerId && !coveringNameFromList,
      staleTime: FIVE_MINUTE_STALE_TIME,
    },
  });

  // Resolved name for display: an optimistic name captured at save time wins, then the
  // org list, then the direct fetch.
  const displayCoverageName =
    coveragePractitionerName ||
    coveringNameFromList ||
    (fetchedCoveringPractitioner ? formatPractitionerName(fetchedCoveringPractitioner) : "");

  // Once the coverage query catches up with an optimistic save/remove, drop the
  // override (and the captured name) so the display reverts to server-derived state.
  // Otherwise the override would latch for the session and a later external change
  // (e.g. an admin reassigning coverage) would not surface without a refresh.
  useEffect(() => {
    if (optimisticCovered === null) {
      return;
    }
    const serverCovered = coveringPractitionerId != null;
    if (serverCovered === optimisticCovered) {
      setOptimisticCovered(null);
      setCoveragePractitionerName("");
    }
  }, [optimisticCovered, coveringPractitionerId]);

  const practitionerOptions = useMemo(
    () => availablePractitioners.map(p => ({ id: p.id, name: p.name })),
    [availablePractitioners]
  );

  // Logged-in practitioner's current alert email preference, phrased for the
  // coverage summary row (e.g. "Alert emails paused").
  const emailPreferenceSummaryLabel = useMemo(() => {
    switch (preference) {
      case AlertEmailPreference.ALL_ALERTS:
        return translate("pages.profile.settings.coverage.emailPrefSummary.allAlerts");
      case AlertEmailPreference.RED_ALERTS:
        return translate("pages.profile.settings.coverage.emailPrefSummary.redAlerts");
      default:
        return translate("pages.profile.settings.coverage.emailPrefSummary.none");
    }
  }, [preference, translate]);

  const handleAlertPreferenceChange = async (event: SelectChangeEvent) => {
    const newPreference = event.target.value as AlertEmailPreference;
    const success = await updatePreference(newPreference);
    if (success) {
      openNotification?.({
        type: "success",
        message: translate("pages.profile.settings.notifications.success"),
      });
    } else {
      openNotification?.({
        type: "error",
        message: translate("pages.profile.settings.notifications.error"),
      });
    }
  };

  // Persist the logged-in practitioner's own alert email preference chosen in the
  // dialog. A failure here does not roll back the coverage change — surface a soft
  // error and capture it for observability.
  const persistEmailPreference = async () => {
    const prefOk = await updatePreference(dialogEmailPreference);
    if (!prefOk) {
      openNotification?.({
        type: "error",
        message: translate("pages.profile.settings.notifications.error"),
      });
      Sentry.captureException(new Error("Failed to persist coverage email preference"), {
        level: "warning",
        tags: { feature: "coverage", operation: "self-set-email-preferences" },
      });
    }
  };

  const handleAddCoverageClick = () => {
    setSelectedClinician("");
    setDialogEmailPreference(AlertEmailPreference.NONE);
    setCoverageConfirmAction("add");
    setCoverageConfirmOpen(true);
  };

  const handleRemoveCoverage = () => {
    setDialogEmailPreference(AlertEmailPreference.ALL_ALERTS);
    setCoverageConfirmAction("remove");
    setCoverageConfirmOpen(true);
  };

  const handleCoverageConfirmCancel = () => {
    setCoverageConfirmOpen(false);
  };

  const handleCoverageConfirm = async () => {
    setCoverageConfirmOpen(false);

    if (coverageConfirmAction === "add") {
      const practitioner = availablePractitioners.find(p => p.id === selectedClinician);
      if (!practitioner) {
        return;
      }

      const result = await setCoverage(practitionerId!, selectedClinician);
      if (result.success) {
        setCoveragePractitionerName(practitioner.name);
        setOptimisticCovered(true);
        openNotification?.({
          type: "success",
          message: translate("pages.profile.settings.coverage.saveSuccess"),
        });
        await persistEmailPreference();
      } else {
        openNotification?.({
          type: "error",
          message: translate(
            result.conflict
              ? "pages.profile.settings.coverage.alreadyAssigned"
              : "pages.profile.settings.coverage.saveError"
          ),
        });
      }
    } else {
      if (!coveringPractitionerId) {
        return;
      }
      const success = await removeCoverage(practitionerId!, coveringPractitionerId);
      if (success) {
        setSelectedClinician("");
        setCoveragePractitionerName("");
        setOptimisticCovered(false);
        openNotification?.({
          type: "success",
          message: translate("pages.profile.settings.coverage.removeSuccess"),
        });
        await persistEmailPreference();
      } else {
        openNotification?.({
          type: "error",
          message: translate("pages.profile.settings.coverage.removeError"),
        });
      }
    }
  };

  return (
    <>
      <Typography variant="body2" color="text.primary" mb={0.5}>
        {translate("pages.profile.nav.settings")}
      </Typography>
      <Typography variant="subtitle2" mb={2}>
        {translate("pages.profile.settings.subtitle")}
      </Typography>

      <Stack spacing={2}>
        {/* Notifications Section */}
        <Stack spacing={1}>
          <Typography variant="overline" color="text.hint">
            {translate("pages.profile.settings.notifications.sectionTitle")}
          </Typography>
          <Paper variant="outlined">
            <Stack p={2} spacing={1} alignItems="center">
              <Box width="100%">
                <Typography variant="body1" color="text.primary">
                  {translate("pages.profile.settings.notifications.alertPreferencesTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate("pages.profile.settings.notifications.alertPreferencesSubtitle")}
                </Typography>
              </Box>
              <Box width={220}>
                {isLoading ? (
                  <Stack alignItems="center" py={1}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : (
                  <Tooltip
                    title={!hasPermission ? translate("pages.profile.settings.notifications.noPermission") : ""}
                    arrow
                  >
                    <span style={!hasPermission ? { cursor: "not-allowed" } : undefined}>
                      <Select
                        fullWidth
                        value={preference}
                        onChange={handleAlertPreferenceChange}
                        size="small"
                        disabled={isUpdating || !hasPermission}
                      >
                        <MenuItem value={AlertEmailPreference.ALL_ALERTS}>
                          {translate("pages.profile.settings.notifications.preferences.allAlerts")}
                        </MenuItem>
                        <MenuItem value={AlertEmailPreference.RED_ALERTS}>
                          {translate("pages.profile.settings.notifications.preferences.redAlerts")}
                        </MenuItem>
                        <MenuItem value={AlertEmailPreference.NONE}>
                          {translate("pages.profile.settings.notifications.preferences.none")}
                        </MenuItem>
                      </Select>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Stack>
          </Paper>
        </Stack>

        {/* Coverage Section */}
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="overline" color="text.hint">
              {translate("pages.profile.settings.coverage.sectionTitle")}
            </Typography>
            {isCovered && (
              <Box
                bgcolor="info.badgeActive"
                border={1}
                borderColor="info.dark"
                borderRadius={1}
                px={1}
                display="flex"
                alignItems="center"
              >
                <Typography variant="caption" color="text.primary">
                  {translate("pages.profile.settings.coverage.active")}
                </Typography>
              </Box>
            )}
          </Stack>
          <Paper variant="outlined">
            <Stack p={2} spacing={2} alignItems="center">
              <Box width="100%">
                <Typography variant="body1" color="text.primary">
                  {translate("pages.profile.settings.coverage.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate("pages.profile.settings.coverage.subtitle")}
                </Typography>
              </Box>

              {isCoverageLoading ? (
                <Stack alignItems="center" py={1}>
                  <CircularProgress size={24} />
                </Stack>
              ) : !isCovered ? (
                <CardButton fullWidth={false} height={36} onClick={handleAddCoverageClick}>
                  {translate("pages.profile.settings.coverage.addCoverage")}
                </CardButton>
              ) : (
                <Box width="100%" bgcolor="background.default" borderRadius={1.5} p={2}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box display="flex" color="text.secondary">
                      <Person color="inherit" />
                    </Box>
                    <Stack flexGrow={1} spacing={0} minWidth={0}>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="text.primary"
                        data-testid="coverage-practitioner-name"
                      >
                        {displayCoverageName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {emailPreferenceSummaryLabel}
                      </Typography>
                    </Stack>
                    <Button variant="dialog" color="error" onClick={handleRemoveCoverage} disabled={isSaving}>
                      {translate("pages.profile.settings.coverage.removeCoverage")}
                    </Button>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      {coverageConfirmAction === "add" ? (
        <AddCoverageDialog
          open={coverageConfirmOpen}
          onConfirm={handleCoverageConfirm}
          onCancel={handleCoverageConfirmCancel}
          translationPrefix="pages.profile.settings.coverage"
          practitionerOptions={practitionerOptions}
          selectedPractitionerId={selectedClinician}
          onSelectedPractitionerChange={setSelectedClinician}
          emailPreference={dialogEmailPreference}
          onEmailPreferenceChange={setDialogEmailPreference}
          confirmDisabled={isLoading}
          isSaving={isSaving}
        />
      ) : (
        <RemoveCoverageDialog
          open={coverageConfirmOpen}
          onConfirm={handleCoverageConfirm}
          onCancel={handleCoverageConfirmCancel}
          translationPrefix="pages.profile.settings.coverage"
          coveragePractitionerName={displayCoverageName}
          emailPreference={dialogEmailPreference}
          onEmailPreferenceChange={setDialogEmailPreference}
          confirmDisabled={isLoading}
          isSaving={isSaving}
        />
      )}
    </>
  );
};
